import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api } from '../lib/api';
import { PlusIcon, CheckIcon } from '../components/Icons';

const StatusBadge = ({ status }) => {
  if (status === 'stopped') {
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
        Closed
      </span>
    );
  }
  return (
    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-[#ecf8fb] text-[#2a798c]">
      Active
    </span>
  );
};

const Campaigns = () => {
  const { profile, user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [registering, setRegistering] = useState({});
  const [selectedCampaigns, setSelectedCampaigns] = useState(new Set());
  const [closing, setClosing] = useState(false);

  const searchParams = new URLSearchParams(location.search);
  const showMyCampaigns = searchParams.get('my_campaigns') === 'true';

  useEffect(() => {
    loadCampaigns();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.search]);

  const loadCampaigns = async () => {
    try {
      const data = await api.get('/api/campaigns');
      if (showMyCampaigns && (profile?.role === 'hunter' || profile?.role === 'basic')) {
        setCampaigns(data.filter((c) => c.is_registered === true));
      } else {
        setCampaigns(data);
      }
    } catch (error) {
      // silently fail
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e, campaignId) => {
    e.preventDefault();
    e.stopPropagation();
    setRegistering({ ...registering, [campaignId]: true });
    try {
      await api.post(`/api/campaigns/${campaignId}/register`);
      alert('Berhasil mendaftar ke campaign!');
      navigate(`/campaigns/${campaignId}`);
    } catch (error) {
      alert(error.message || 'Gagal mendaftar ke campaign');
    } finally {
      setRegistering({ ...registering, [campaignId]: false });
    }
  };

  const handleToggleSelect = (campaignId) => {
    const next = new Set(selectedCampaigns);
    if (next.has(campaignId)) next.delete(campaignId);
    else next.add(campaignId);
    setSelectedCampaigns(next);
  };

  const handleSelectAll = (list) => {
    const selectable = list
      .filter((c) => {
        const isActive = c.status === 'active' || !c.status;
        return isActive && (profile?.role === 'admin' || (profile?.role === 'campaign_manager' && c.created_by === user?.id));
      })
      .map((c) => c.id);
    if (selectable.every((id) => selectedCampaigns.has(id))) {
      setSelectedCampaigns(new Set());
    } else {
      setSelectedCampaigns(new Set(selectable));
    }
  };

  const handleBulkClose = async () => {
    if (selectedCampaigns.size === 0) return alert('Pilih campaign yang ingin ditutup');
    if (!window.confirm(`Tutup ${selectedCampaigns.size} campaign yang dipilih?`)) return;
    setClosing(true);
    try {
      const res = await api.patch('/api/campaigns/bulk-stop', { campaign_ids: Array.from(selectedCampaigns) });
      alert(`Berhasil menutup ${res.closed_count} campaign!`);
      setSelectedCampaigns(new Set());
      loadCampaigns();
    } catch (error) {
      alert(error.message || 'Gagal menutup campaign');
    } finally {
      setClosing(false);
    }
  };

  const activeCampaigns = campaigns.filter((c) => c.status === 'active' || !c.status);
  const closedCampaigns = campaigns.filter((c) => c.status === 'stopped');
  const selectableCampaigns = activeCampaigns.filter(
    (c) => profile?.role === 'admin' || (profile?.role === 'campaign_manager' && c.created_by === user?.id)
  );
  const allSelected = selectableCampaigns.length > 0 && selectableCampaigns.every((c) => selectedCampaigns.has(c.id));

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-2 border-[#3fb5d2] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const renderCampaignCard = (campaign) => {
    const isActive = campaign.status === 'active' || !campaign.status;
    const isSelected = selectedCampaigns.has(campaign.id);
    const canSelect = isActive && (
      profile?.role === 'admin' ||
      (profile?.role === 'campaign_manager' && campaign.created_by === user?.id)
    );

    return (
      <div
        key={campaign.id}
        className={`bg-white rounded-2xl flex flex-col overflow-hidden transition-shadow hover:shadow-sinii-md ${
          isSelected ? 'ring-2 ring-[#3fb5d2]' : ''
        }`}
        style={{ boxShadow: '0px 2px 8px 0px rgba(0,0,0,0.04)' }}
      >
        {/* Campaign image */}
        {campaign.image_url && (
          <Link to={`/campaigns/${campaign.id}`} className="block">
            <img
              src={campaign.image_url}
              alt={campaign.name}
              className="w-full h-36 object-cover"
            />
          </Link>
        )}

        <div className="flex-1 flex flex-col p-5">
          {/* Header row */}
          <div className="flex items-start justify-between gap-2 mb-2">
            {canSelect && (
              <input
                type="checkbox"
                checked={isSelected}
                onChange={() => handleToggleSelect(campaign.id)}
                onClick={(e) => e.stopPropagation()}
                className="mt-1 h-4 w-4 rounded border-[#e5e5e5] text-[#3fb5d2] focus:ring-[#3fb5d2] flex-shrink-0"
              />
            )}
            <Link to={`/campaigns/${campaign.id}`} className="flex-1 min-w-0">
              <h3 className="text-[16px] font-semibold text-[#3b3b3b] leading-6 truncate">
                {campaign.name}
              </h3>
            </Link>
            <StatusBadge status={campaign.status} />
          </div>

          <Link to={`/campaigns/${campaign.id}`} className="block">
            <p className="text-[14px] text-[#646464] leading-5 line-clamp-2 mb-4">
              {campaign.description}
            </p>
          </Link>

          {/* Proposal stats */}
          {!(profile?.role === 'basic' && !campaign.is_registered) && (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2 gap-y-3 text-center py-3 border-t border-b border-[#e5e5e5] mb-4">
              {[
                { label: 'Submitted', value: campaign.proposal_stats?.submitted || 0, color: 'text-[#3b3b3b]' },
                { label: 'Pending', value: campaign.proposal_stats?.pending || 0, color: 'text-[#F59E0B]' },
                { label: 'Approved', value: campaign.proposal_stats?.approved || 0, color: 'text-[#10B981]' },
                { label: 'Verified', value: campaign.proposal_stats?.verified || 0, color: 'text-[#8B5CF6]' },
                { label: 'Monitored', value: campaign.proposal_stats?.monitored || 0, color: 'text-[#3fb5d2]' },
              ].map(({ label, value, color }) => (
                <div key={label}>
                  <div className={`text-[15px] font-semibold ${color}`}>{value}</div>
                  <div className="text-[10px] text-[#8a8a8a] leading-3 mt-0.5">{label}</div>
                </div>
              ))}
            </div>
          )}

          {/* Meta */}
          <div className="mt-auto space-y-1">
            <p className="text-[12px] text-[#8a8a8a]">
              By {campaign.user_profiles?.full_name || 'Unknown'}
            </p>
            <p className="text-[12px] text-[#8a8a8a]">
              {new Date(campaign.created_at).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' })}
            </p>
            {campaign.participant_quota !== null && campaign.participant_quota !== undefined && (
              <p className="text-[12px] text-[#8a8a8a]">
                Links: {campaign.link_count ?? campaign.participant_count ?? 0} / {campaign.participant_quota}
                {campaign.is_quota_full && (
                  <span className="ml-1 text-[#c62828] font-medium">(Penuh)</span>
                )}
              </p>
            )}
          </div>

          {/* Register button for hunters */}
          {(profile?.role === 'hunter' || profile?.role === 'basic') && (
            <div className="mt-4 pt-4 border-t border-[#e5e5e5]">
              {campaign.status === 'stopped' ? (
                <div className="text-center">
                  <span className="text-[13px] text-[#8a8a8a] font-medium">Campaign Closed</span>
                </div>
              ) : campaign.is_registered ? (
                <div className="flex items-center justify-center gap-1.5 text-[#2a798c]">
                  <CheckIcon />
                  <span className="text-[13px] font-medium">Sudah Terdaftar</span>
                </div>
              ) : (
                <button
                  onClick={(e) => handleRegister(e, campaign.id)}
                  disabled={registering[campaign.id]}
                  className="w-full bg-[#3fb5d2] hover:bg-[#2a798c] text-white text-[13px] font-semibold py-2 rounded-lg transition-colors disabled:opacity-50"
                >
                  {registering[campaign.id] ? 'Mendaftar...' : 'Join Campaign'}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {/* Page header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <h1 className="text-[22px] font-semibold text-[#3b3b3b] leading-8">
            {showMyCampaigns ? 'Your Campaigns' : 'Campaigns'}
          </h1>
          {showMyCampaigns && (
            <p className="text-[13px] text-[#8a8a8a] mt-0.5">Campaign yang sudah Anda ikuti</p>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {showMyCampaigns && (
            <Link
              to="/campaigns"
              className="text-[13px] text-[#3fb5d2] hover:text-[#2a798c] font-medium transition-colors"
            >
              Lihat Semua
            </Link>
          )}
          {(profile?.role === 'campaign_manager' || profile?.role === 'admin') && (
            <Link
              to="/campaigns/create"
              className="flex items-center gap-2 bg-[#3fb5d2] hover:bg-[#2a798c] text-white text-[13px] font-semibold px-4 py-2 rounded-lg transition-colors"
            >
              <PlusIcon />
              Create Campaign
            </Link>
          )}
        </div>
      </div>

      {/* Bulk actions */}
      {(profile?.role === 'admin' || profile?.role === 'campaign_manager') &&
        selectableCampaigns.length > 0 && (
          <div
            className="bg-white rounded-2xl px-4 py-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between"
            style={{ boxShadow: '0px 2px 8px 0px rgba(0,0,0,0.04)' }}
          >
            <div className="flex items-center gap-3 min-w-0">
              <input
                type="checkbox"
                checked={allSelected}
                onChange={() => handleSelectAll(activeCampaigns)}
                className="h-4 w-4 rounded border-[#e5e5e5] text-[#3fb5d2] focus:ring-[#3fb5d2]"
              />
              <span className="text-[13px] text-[#646464]">
                {selectedCampaigns.size > 0
                  ? `${selectedCampaigns.size} campaign dipilih`
                  : 'Pilih semua campaign aktif'}
              </span>
            </div>
            {selectedCampaigns.size > 0 && (
              <button
                onClick={handleBulkClose}
                disabled={closing}
                className="bg-[#c62828] hover:bg-red-700 text-white text-[13px] font-medium px-4 py-1.5 rounded-lg transition-colors disabled:opacity-50"
              >
                {closing ? 'Menutup...' : `Tutup ${selectedCampaigns.size} Campaign`}
              </button>
            )}
          </div>
        )}

      {/* Active campaigns */}
      {activeCampaigns.length > 0 && (
        <div>
          <h2 className="text-[15px] font-semibold text-[#646464] uppercase tracking-wide mb-3">
            Active — {activeCampaigns.length}
          </h2>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
            {activeCampaigns.map(renderCampaignCard)}
          </div>
        </div>
      )}

      {/* Closed campaigns */}
      {closedCampaigns.length > 0 && (
        <div>
          <h2 className="text-[15px] font-semibold text-[#646464] uppercase tracking-wide mb-3">
            Closed — {closedCampaigns.length}
          </h2>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
            {closedCampaigns.map(renderCampaignCard)}
          </div>
        </div>
      )}

      {/* Empty state */}
      {campaigns.length === 0 && (
        <div
          className="bg-white rounded-2xl p-12 text-center"
          style={{ boxShadow: '0px 2px 8px 0px rgba(0,0,0,0.04)' }}
        >
          <p className="text-[#8a8a8a] text-[14px]">Belum ada campaign.</p>
          {(profile?.role === 'campaign_manager' || profile?.role === 'admin') && (
            <Link
              to="/campaigns/create"
              className="mt-3 inline-block text-[#3fb5d2] hover:text-[#2a798c] text-[14px] font-medium"
            >
              Buat campaign pertama
            </Link>
          )}
        </div>
      )}
    </div>
  );
};

export default Campaigns;
